import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 10050,
  background: 'var(--modal-overlay-bg)',
  backdropFilter: 'blur(4px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '16px',
};

const panelStyle: React.CSSProperties = {
  backgroundColor: 'var(--bg-card)',
  border: '2px solid #F59E0B',
  borderRadius: '16px',
  width: '100%',
  maxWidth: '480px',
  boxShadow: 'var(--modal-panel-shadow)',
};

const AMBER = '#F59E0B';

function formatCountdown(totalSeconds: number): string {
  const s = Math.max(0, Math.min(3599, Math.floor(totalSeconds)));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

export type SessionTimeoutModalProps = {
  open: boolean;
  secondsRemaining: number;
  onStayLoggedIn: () => void;
  onLogoutNow: () => void;
};

export function SessionTimeoutModal({
  open,
  secondsRemaining,
  onStayLoggedIn,
  onLogoutNow,
}: SessionTimeoutModalProps) {
  const titleId = useId();
  const descId = useId();
  const countdownId = useId();
  const stayRef = useRef<HTMLButtonElement>(null);

  const safeSeconds =
    typeof secondsRemaining === 'number' && Number.isFinite(secondsRemaining)
      ? Math.max(0, Math.floor(secondsRemaining))
      : 0;

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => stayRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
      }
    }
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      role="presentation"
      style={overlayStyle}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-live="assertive"
        aria-labelledby={titleId}
        aria-describedby={`${descId} ${countdownId}`}
        style={panelStyle}
      >
        <div className="border-b border-border px-5 py-4" style={{ borderColor: `${AMBER}55` }}>
          <h2 id={titleId} className="text-lg font-semibold text-foreground">
            Session timeout
          </h2>
        </div>
        <div className="space-y-4 px-5 py-5 text-sm text-foreground">
          <p id={descId}>
            You&apos;ve been inactive. You will be logged out in{' '}
            <span
              id={countdownId}
              className="font-mono font-semibold tabular-nums"
              style={{ color: AMBER }}
            >
              {formatCountdown(safeSeconds)}
            </span>
            .
          </p>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
            <button
              ref={stayRef}
              type="button"
              onClick={() => onStayLoggedIn()}
              className="flex min-h-[44px] flex-1 items-center justify-center rounded-lg px-4 py-3 text-sm font-semibold text-cta-text transition hover:opacity-90"
              style={{ backgroundColor: 'var(--accent-green, #059669)' }}
            >
              Stay logged in
            </button>
            <button
              type="button"
              onClick={() => onLogoutNow()}
              className="text-center text-xs font-medium text-muted underline-offset-2 transition hover:text-foreground hover:underline sm:self-center sm:px-3"
            >
              Log out now
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
